import React, { useState } from "react";
import { CheckCircle2, Plus, AlertTriangle, Home, Lightbulb, Box, Layers } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Toggle } from "../ui/toggle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Section, FormField } from "./InspectionShared";

const ActionsSection = ({
  formData, setFormData, toggleArray, handleSectionPhoto,
  actionCategories, availableActionTypes, hive, allHives, allYards,
  availableQueens = [],
  showSuggestAction, setShowSuggestAction, suggestActionName, setSuggestActionName,
  suggestActionCategory, setSuggestActionCategory, handleSuggestAction, submittingSuggestion
}) => {
  // Compute available hives for split destination (other hives, preferring same yard)
  const emptyHives = (allHives || []).filter(h => h.id !== hive?.id);

  return (
        <Section title="Actions Taken" icon={<CheckCircle2 className="h-6 w-6 text-lime-600" />} onPhoto={handleSectionPhoto}>
          {/* Dynamic Actions by Category */}
          {actionCategories.length > 0 && availableActionTypes.length > 0 ? (
            <div className="space-y-4">
              {actionCategories.map(category => {
                // Skip brood_management and super_management - they have dedicated sections below
                if (category.id === 'brood_management' || category.id === 'super_management') return null;
                const categoryActions = availableActionTypes.filter(a => a.category === category.id);
                if (categoryActions.length === 0) return null;
                
                const categoryColors = {
                  amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-800' },
                  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
                  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-800' },
                  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800' },
                  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-800' },
                  stone: { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-800' }
                };
                const colors = categoryColors[category.color] || categoryColors.stone;
                
                // Check if this is the equipment category with add/remove pairs
                const hasEquipmentPairs = category.id === 'equipment' || category.id === 'harvest' || category.id === 'pest_control';
                const addActions = categoryActions.filter(a => a.action_type === 'add');
                const removeActions = categoryActions.filter(a => a.action_type === 'remove');
                const otherActions = categoryActions.filter(a => !a.action_type);
                
                // Build equipment pairs
                const equipmentPairs = [];
                addActions.forEach(addAction => {
                  const itemName = addAction.name.replace('Added ', '');
                  const removeAction = removeActions.find(r => r.name.replace('Removed ', '') === itemName);
                  equipmentPairs.push({
                    name: itemName,
                    addKey: addAction.name,
                    removeKey: removeAction?.name || `Removed ${itemName}`,
                    hasQty: addAction.has_qty || removeAction?.has_qty,
                    qtyKey: itemName.toLowerCase().replace(/\s+/g, '_') + '_qty'
                  });
                });
                
                return (
                  <div key={category.id} className={`p-4 rounded-xl border ${colors.bg} ${colors.border}`}>
                    <Label className={`font-semibold mb-3 block ${colors.text}`}>{category.name}</Label>
                    
                    {/* Equipment-style add/remove table */}
                    {hasEquipmentPairs && equipmentPairs.length > 0 && (
                      <div className="overflow-x-auto mb-3">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-stone-300">
                              <th className="text-left py-2 px-2 font-semibold text-stone-700">Item</th>
                              <th className="text-center py-2 px-2 font-semibold text-lime-700 w-32">Added</th>
                              <th className="text-center py-2 px-2 font-semibold text-red-700 w-32">Removed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {equipmentPairs.map((item) => (
                              <tr key={item.name} className="border-b border-stone-200">
                                <td className="py-2 px-2 font-medium text-stone-700">{item.name}</td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center justify-center gap-1">
                                    <Toggle
                                      pressed={formData.actions.includes(item.addKey)}
                                      onPressedChange={() => {
                                        toggleArray('actions', item.addKey);
                                        if (formData.actions.includes(item.removeKey)) {
                                          toggleArray('actions', item.removeKey);
                                        }
                                      }}
                                      className="h-10 w-12 data-[state=on]:bg-lime-600 data-[state=on]:text-white text-xs"
                                    >
                                      ✓
                                    </Toggle>
                                    {item.hasQty && formData.actions.includes(item.addKey) && (
                                      <Input
                                        type="number"
                                        min="1"
                                        value={formData[`added_${item.qtyKey}`] || ''}
                                        onChange={(e) => setFormData({ ...formData, [`added_${item.qtyKey}`]: e.target.value })}
                                        placeholder="#"
                                        className="h-10 w-14 text-center text-xs"
                                      />
                                    )}
                                  </div>
                                </td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center justify-center gap-1">
                                    <Toggle
                                      pressed={formData.actions.includes(item.removeKey)}
                                      onPressedChange={() => {
                                        toggleArray('actions', item.removeKey);
                                        if (formData.actions.includes(item.addKey)) {
                                          toggleArray('actions', item.addKey);
                                        }
                                      }}
                                      className="h-10 w-12 data-[state=on]:bg-red-600 data-[state=on]:text-white text-xs"
                                    >
                                      ✓
                                    </Toggle>
                                    {item.hasQty && formData.actions.includes(item.removeKey) && (
                                      <Input
                                        type="number"
                                        min="1"
                                        value={formData[`removed_${item.qtyKey}`] || ''}
                                        onChange={(e) => setFormData({ ...formData, [`removed_${item.qtyKey}`]: e.target.value })}
                                        placeholder="#"
                                        className="h-10 w-14 text-center text-xs"
                                      />
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {/* Other non-paired actions in this category */}
                    {otherActions.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {otherActions.map((action) => (
                          <Toggle
                            key={action.id}
                            pressed={formData.actions.includes(action.name)}
                            onPressedChange={() => toggleArray('actions', action.name)}
                            data-testid={`action-${action.name.toLowerCase().replace(/\s+/g, '-')}`}
                            className="h-12 px-4 data-[state=on]:bg-lime-600 data-[state=on]:text-white text-sm"
                          >
                            {action.name}
                          </Toggle>
                        ))}
                      </div>
                    )}

                    {/* Special Actions Detail Forms - render INSIDE the special category div */}
                    {category.id === 'special' && (
                      <>
                        {/* Split Hive Details */}
                        {formData.actions.includes('Split Hive') && (
                          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                            <div className="font-semibold text-amber-800">🔀 Split Hive Details</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <Label className="text-xs text-stone-600">Removed Capped Brood Frame #</Label>
                                <Input
                                  type="text"
                                  value={formData.split_removed_brood_frame}
                                  onChange={(e) => setFormData({ ...formData, split_removed_brood_frame: e.target.value })}
                                  placeholder="e.g., Frame 3"
                                  className="h-10 mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-stone-600">Removed Pollen Frame #</Label>
                                <Input
                                  type="text"
                                  value={formData.split_removed_pollen_frame}
                                  onChange={(e) => setFormData({ ...formData, split_removed_pollen_frame: e.target.value })}
                                  placeholder="e.g., Frame 5"
                                  className="h-10 mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-stone-600">Removed Honey Frame #</Label>
                                <Input
                                  type="text"
                                  value={formData.split_removed_honey_frame}
                                  onChange={(e) => setFormData({ ...formData, split_removed_honey_frame: e.target.value })}
                                  placeholder="e.g., Frame 8"
                                  className="h-10 mt-1"
                                />
                              </div>
                            </div>

                            {/* Destination Hive */}
                            <div className="border-t border-amber-200 pt-3 mt-3">
                              <Label className="text-xs font-semibold text-amber-800 mb-2 block">Destination Hive</Label>
                              <div className="flex gap-2 mb-3">
                                <Toggle
                                  pressed={formData.split_destination === 'existing'}
                                  onPressedChange={() => setFormData(prev => ({ ...prev, split_destination: 'existing', split_destination_hive_id: '' }))}
                                  className="h-9 px-3 text-xs data-[state=on]:bg-amber-600 data-[state=on]:text-white"
                                >
                                  Existing Hive
                                </Toggle>
                                <Toggle
                                  pressed={formData.split_destination === 'new'}
                                  onPressedChange={() => setFormData(prev => ({ ...prev, split_destination: 'new', split_destination_hive_id: '' }))}
                                  className="h-9 px-3 text-xs data-[state=on]:bg-amber-600 data-[state=on]:text-white"
                                >
                                  Create New Hive
                                </Toggle>
                              </div>

                              {formData.split_destination === 'existing' && (
                                <Select
                                  value={formData.split_destination_hive_id}
                                  onValueChange={(val) => setFormData(prev => ({ ...prev, split_destination_hive_id: val }))}
                                >
                                  <SelectTrigger className="h-10">
                                    <SelectValue placeholder="Select destination hive..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {emptyHives.map(h => (
                                      <SelectItem key={h.id} value={h.id}>
                                        Hive #{h.hive_number} {h.nickname ? `(${h.nickname})` : ''} — {h.yard}
                                      </SelectItem>
                                    ))}
                                    {emptyHives.length === 0 && (
                                      <SelectItem value="_none" disabled>No hives available in this yard</SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              )}

                              {formData.split_destination === 'new' && (
                                <div className="bg-amber-100 rounded-lg p-3 text-sm text-amber-800">
                                  A new hive will be auto-created in <strong>{hive?.yard || 'this yard'}</strong> with origin &quot;Split from Hive #{hive?.hive_number}&quot;
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Requeened Details */}
                        {formData.actions.includes('Requeened') && (
                          <div className="mt-4 bg-lime-50 border border-lime-200 rounded-xl p-4 space-y-3">
                            <div className="font-semibold text-lime-800">👑 Requeened Details</div>
                            <div>
                              <Label className="text-xs text-stone-600">Requeened Date</Label>
                              <Input
                                type="date"
                                value={formData.requeened_date}
                                onChange={(e) => setFormData({ ...formData, requeened_date: e.target.value })}
                                className="h-10 mt-1"
                              />
                            </div>

                            {/* Queen Source */}
                            <div>
                              <Label className="text-xs font-semibold text-lime-800 mb-2 block">Queen Source</Label>
                              <div className="flex flex-wrap gap-2 mb-3">
                                {[
                                  { value: 'queen_batch', label: '🧬 From Breeding Batch' },
                                  { value: 'purchased', label: '🛒 Purchased' },
                                  { value: 'emergency_cell', label: '🚨 Emergency Queen Cell' },
                                  { value: 'other', label: '📝 Other' },
                                ].map(opt => (
                                  <Toggle
                                    key={opt.value}
                                    pressed={formData.requeened_source === opt.value}
                                    onPressedChange={() => setFormData(prev => ({ ...prev, requeened_source: opt.value, requeened_queen_cell_id: '', requeened_batch_id: '' }))}
                                    className="h-9 px-3 text-xs data-[state=on]:bg-lime-600 data-[state=on]:text-white"
                                  >
                                    {opt.label}
                                  </Toggle>
                                ))}
                              </div>

                              {formData.requeened_source === 'queen_batch' && (
                                <div className="space-y-2">
                                  {availableQueens.length > 0 ? (
                                    <Select
                                      value={formData.requeened_queen_cell_id}
                                      onValueChange={(val) => {
                                        const q = availableQueens.find(q => q.cell_id === val);
                                        setFormData(prev => ({
                                          ...prev,
                                          requeened_queen_cell_id: val,
                                          requeened_batch_id: q?.batch_id || '',
                                          requeened_queen_breed: q?.breed || ''
                                        }));
                                      }}
                                    >
                                      <SelectTrigger className="h-10">
                                        <SelectValue placeholder="Select a queen from your batches..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {availableQueens.map(q => (
                                          <SelectItem key={q.cell_id} value={q.cell_id}>
                                            {q.batch_name} — Cell #{q.cell_number} ({q.status}{q.breed ? `, ${q.breed}` : ''})
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <div className="bg-lime-100 rounded-lg p-3 text-sm text-lime-700">
                                      No available queens found in your breeding batches. Queens must be in &quot;emerged&quot;, &quot;mated&quot;, or &quot;laying&quot; status and not yet assigned to a hive.
                                    </div>
                                  )}
                                </div>
                              )}

                              {formData.requeened_source === 'purchased' && (
                                <Input
                                  value={formData.requeened_queen_breed}
                                  onChange={(e) => setFormData(prev => ({ ...prev, requeened_queen_breed: e.target.value }))}
                                  placeholder="Queen breed/supplier, e.g. Italian queen from XYZ Apiaries"
                                  className="h-10"
                                />
                              )}

                              {(formData.requeened_source === 'emergency_cell' || formData.requeened_source === 'other') && (
                                <Input
                                  value={formData.requeened_notes}
                                  onChange={(e) => setFormData(prev => ({ ...prev, requeened_notes: e.target.value }))}
                                  placeholder={formData.requeened_source === 'emergency_cell' ? 'Describe the emergency queen cell situation...' : 'Notes about the new queen...'}
                                  className="h-10"
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {/* Winter Pack Down Details */}
                        {formData.actions.includes('Winter Pack Down') && (
                          <div className="mt-4 bg-stone-100 border border-stone-300 rounded-xl p-4">
                            <Label className="text-xs text-stone-600"># of Boxes Packed Down</Label>
                            <Input
                              type="number"
                              min="1"
                              value={formData.winter_pack_boxes}
                              onChange={(e) => setFormData({ ...formData, winter_pack_boxes: e.target.value })}
                              placeholder="Enter number of boxes"
                              className="h-12 mt-1"
                            />
                          </div>
                        )}

                        {/* Combined Hives Details */}
                        {formData.actions.includes('Combined Hives') && (
                          <div className="mt-4 bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-4">
                            <div className="font-semibold text-purple-800">🔗 Combined Hives Details</div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-stone-600">Primary Hive (receiving bees)</Label>
                                <div className="text-sm text-purple-700 font-medium mt-1 bg-purple-100 p-2 rounded">
                                  This Hive ({hive?.hive_number || 'Current'})
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs text-stone-600">Source Hive (being combined)</Label>
                                <Select 
                                  value={formData.combine_source_hive} 
                                  onValueChange={(value) => setFormData({ ...formData, combine_source_hive: value })}
                                >
                                  <SelectTrigger className="h-12 mt-1">
                                    <SelectValue placeholder="Select hive to combine..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allHives
                                      .filter(h => h.id !== hive?.id && h.status === 'active')
                                      .map(h => (
                                        <SelectItem key={h.id} value={h.id}>
                                          Hive {h.hive_number} {h.nickname ? `(${h.nickname})` : ''} - {allYards.find(y => y.id === h.yard_id)?.name || 'Unknown Yard'}
                                        </SelectItem>
                                      ))
                                    }
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div>
                              <Label className="text-xs text-stone-600">What to do with the empty hive?</Label>
                              <Select 
                                value={formData.combine_empty_hive_action} 
                                onValueChange={(value) => setFormData({ ...formData, combine_empty_hive_action: value })}
                              >
                                <SelectTrigger className="h-12 mt-1">
                                  <SelectValue placeholder="Select action for empty hive..." />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="inactive">Mark as Inactive (no longer occupied)</SelectItem>
                                  <SelectItem value="storage">Taken Back to Storage</SelectItem>
                                  <SelectItem value="disassemble">Disassemble Equipment</SelectItem>
                                  <SelectItem value="repurpose">Repurpose for Split/Swarm</SelectItem>
                                  <SelectItem value="sell">Marked for Sale</SelectItem>
                                  <SelectItem value="other">Other (specify in notes)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            <div>
                              <Label className="text-xs text-stone-600">Additional Notes</Label>
                              <Textarea
                                value={formData.combine_empty_hive_notes}
                                onChange={(e) => setFormData({ ...formData, combine_empty_hive_notes: e.target.value })}
                                placeholder="e.g., Queen was failing, weak colony combined with strong one..."
                                className="mt-1"
                                rows={2}
                              />
                            </div>

                            <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm text-purple-800">
                              ⚠️ <strong>Note:</strong> If you mark the source hive as &quot;Inactive&quot;, it will be removed from the Active Hives count.
                            </div>
                          </div>
                        )}

                        {/* Moved Hive Details */}
                        {formData.actions.includes('Moved Hive') && (
                          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
                            <div className="font-semibold text-blue-800">📍 Moved Hive Details</div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-xs text-stone-600">Type of Move</Label>
                                <Select 
                                  value={formData.moved_hive_type} 
                                  onValueChange={(value) => setFormData({ ...formData, moved_hive_type: value })}
                                >
                                  <SelectTrigger className="h-12 mt-1">
                                    <SelectValue placeholder="Select move type..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="same_apiary">Repositioned in Same Apiary</SelectItem>
                                    <SelectItem value="different_apiary">Moved to Different Apiary</SelectItem>
                                    <SelectItem value="new_location">Moved to New Location/Address</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs text-stone-600">Reason for Move</Label>
                                <Select 
                                  value={formData.moved_hive_reason} 
                                  onValueChange={(value) => setFormData({ ...formData, moved_hive_reason: value })}
                                >
                                  <SelectTrigger className="h-12 mt-1">
                                    <SelectValue placeholder="Select reason..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="better_sun">Better Sun Exposure</SelectItem>
                                    <SelectItem value="shade">More Shade Needed</SelectItem>
                                    <SelectItem value="wind_protection">Wind Protection</SelectItem>
                                    <SelectItem value="flooding">Flooding/Drainage Issues</SelectItem>
                                    <SelectItem value="neighbor_complaint">Neighbor Complaint</SelectItem>
                                    <SelectItem value="pesticide_spraying">Pesticide Spraying Nearby</SelectItem>
                                    <SelectItem value="expansion">Apiary Expansion</SelectItem>
                                    <SelectItem value="consolidation">Apiary Consolidation</SelectItem>
                                    <SelectItem value="access">Better Access</SelectItem>
                                    <SelectItem value="forage">Better Forage Location</SelectItem>
                                    <SelectItem value="robbing">Prevent Robbing</SelectItem>
                                    <SelectItem value="other">Other (specify in notes)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            {formData.moved_hive_type === 'same_apiary' && (
                              <div>
                                <Label className="text-xs text-stone-600">New Position in Apiary</Label>
                                <Input
                                  type="text"
                                  value={formData.moved_hive_new_position}
                                  onChange={(e) => setFormData({ ...formData, moved_hive_new_position: e.target.value })}
                                  placeholder="e.g., Row 2, Position 3; Near the oak tree"
                                  className="h-12 mt-1"
                                />
                              </div>
                            )}

                            {formData.moved_hive_type === 'different_apiary' && (
                              <div>
                                <Label className="text-xs text-stone-600">New Apiary</Label>
                                <Select 
                                  value={formData.moved_hive_new_apiary} 
                                  onValueChange={(value) => setFormData({ ...formData, moved_hive_new_apiary: value })}
                                >
                                  <SelectTrigger className="h-12 mt-1">
                                    <SelectValue placeholder="Select new apiary..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allYards
                                      .filter(y => y.id !== hive?.yard_id)
                                      .map(y => (
                                        <SelectItem key={y.id} value={y.id}>
                                          {y.name} {y.address ? `- ${y.address}` : ''}
                                        </SelectItem>
                                      ))
                                    }
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            <div>
                              <Label className="text-xs text-stone-600">Additional Notes</Label>
                              <Textarea
                                value={formData.moved_hive_notes}
                                onChange={(e) => setFormData({ ...formData, moved_hive_notes: e.target.value })}
                                placeholder="e.g., Moved 3 meters to the left for better morning sun..."
                                className="mt-1"
                                rows={2}
                              />
                            </div>

                            <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 text-sm text-blue-800">
                              💡 <strong>Tip:</strong> If moving more than 3 feet but less than 3 miles, do it gradually to prevent foragers getting lost.
                            </div>
                          </div>
                        )}

                        {/* Dead Out / Lost Colony Details */}
                        {formData.actions.includes('Dead Out / Lost Colony') && (
                          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                            <div className="font-semibold text-red-800">💀 Dead Out / Lost Colony</div>
                            <p className="text-xs text-red-600">
                              {formData.dead_out_cause.some(c => ['AFB (American Foulbrood)', 'EFB (European Foulbrood)', 'Pesticide Exposure', 'Nosema', 'Unknown'].includes(c))
                                ? <>This will mark the hive as <strong>Inactive</strong> — equipment may be contaminated.</>
                                : formData.dead_out_cause.length > 0
                                  ? <>This will mark the hive as <strong>Ready (Prepared)</strong> — equipment can be reused.</>
                                  : <>Select a cause. Reusable causes → Ready; contaminated causes → Inactive.</>
                              }
                            </p>

                            <div>
                              <Label className="text-xs font-semibold text-red-800 mb-2 block">Suspected Cause(s) — select all that apply</Label>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  'Varroa Mites', 'Small Hive Beetle (SHB)', 'Wax Moths',
                                  'Starvation', 'Queenless', 'Pesticide Exposure',
                                  'Absconded', 'Robbing', 'AFB (American Foulbrood)',
                                  'EFB (European Foulbrood)', 'Nosema', 'Cold / Exposure', 'Unknown'
                                ].map(cause => (
                                  <Toggle
                                    key={cause}
                                    pressed={formData.dead_out_cause.includes(cause)}
                                    onPressedChange={() => {
                                      setFormData(prev => {
                                        const causes = prev.dead_out_cause.includes(cause)
                                          ? prev.dead_out_cause.filter(c => c !== cause)
                                          : [...prev.dead_out_cause, cause];
                                        return { ...prev, dead_out_cause: causes };
                                      });
                                    }}
                                    className="h-9 px-3 text-xs data-[state=on]:bg-red-600 data-[state=on]:text-white"
                                  >
                                    {cause}
                                  </Toggle>
                                ))}
                              </div>
                            </div>

                            {/* AFB Alert */}
                            {formData.dead_out_cause.includes('AFB (American Foulbrood)') && (
                              <div className="bg-red-200 border-2 border-red-500 rounded-xl p-4 text-red-900">
                                <div className="font-bold text-base mb-2">🔥 AFB ALERT — Notifiable Disease</div>
                                <ul className="list-disc list-inside space-y-1 text-sm">
                                  <li><strong>American Foulbrood (AFB)</strong> is a notifiable disease in most countries</li>
                                  <li>All frames, boxes, and hive components that contacted the infected colony <strong>must be burned</strong></li>
                                  <li>Do <strong>NOT</strong> reuse any equipment — spores can survive 50+ years</li>
                                  <li>Notify your local <strong>apiary inspector / biosecurity authority</strong> immediately</li>
                                  <li>Inspect all neighbouring hives for signs of infection</li>
                                  <li>Sterilise hive tools with scorching or bleach solution</li>
                                </ul>
                                <p className="mt-2 font-semibold text-sm">Tasks will be auto-created when you save this inspection.</p>
                              </div>
                            )}

                            <div>
                              <Label className="text-xs text-stone-600">Additional Observations</Label>
                              <Textarea
                                value={formData.dead_out_notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, dead_out_notes: e.target.value }))}
                                placeholder="Describe what you found... e.g., bees on floor, no stores left, mite debris on bottom board, wax moth webbing..."
                                className="mt-1"
                                rows={3}
                              />
                            </div>

                            <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-sm text-red-800">
                              ⚠️ <strong>On save:</strong> Hive #{hive?.hive_number} will be marked as <strong>Inactive — {formData.dead_out_cause.length > 0 ? formData.dead_out_cause.join(', ') : 'Dead Out'}</strong>. You can reactivate it later from Edit Hive.
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            // Fallback to original hardcoded layout
            <>
              <div className="mb-4">
                <Label className="text-stone-600 text-sm mb-2 block">Special Actions</Label>
                <div className="flex flex-wrap gap-2">
                  {['Split Hive', 'Requeened', 'Winter Pack Down', 'Combined Hives', 'Moved Hive', 'Dead Out / Lost Colony'].map((action) => (
                    <Toggle
                      key={action}
                      pressed={formData.actions.includes(action)}
                      onPressedChange={() => toggleArray('actions', action)}
                      data-testid={`action-${action.toLowerCase().replace(/\s+/g, '-')}`}
                      className="h-12 px-4 data-[state=on]:bg-amber-600 data-[state=on]:text-white text-sm"
                    >
                      {action}
                    </Toggle>
                  ))}
                </div>
              </div>

              {/* Split Hive Details - fallback */}
              {formData.actions.includes('Split Hive') && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                  <div className="font-semibold text-amber-800">🔀 Split Hive Details</div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-stone-600">Removed Capped Brood Frame #</Label>
                      <Input type="text" value={formData.split_removed_brood_frame} onChange={(e) => setFormData({ ...formData, split_removed_brood_frame: e.target.value })} placeholder="e.g., Frame 3" className="h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Removed Pollen Frame #</Label>
                      <Input type="text" value={formData.split_removed_pollen_frame} onChange={(e) => setFormData({ ...formData, split_removed_pollen_frame: e.target.value })} placeholder="e.g., Frame 5" className="h-10 mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Removed Honey Frame #</Label>
                      <Input type="text" value={formData.split_removed_honey_frame} onChange={(e) => setFormData({ ...formData, split_removed_honey_frame: e.target.value })} placeholder="e.g., Frame 8" className="h-10 mt-1" />
                    </div>
                  </div>
                  <div className="border-t border-amber-200 pt-3 mt-3">
                    <Label className="text-xs font-semibold text-amber-800 mb-2 block">Destination Hive</Label>
                    <div className="flex gap-2 mb-3">
                      <Toggle pressed={formData.split_destination === 'existing'} onPressedChange={() => setFormData(prev => ({ ...prev, split_destination: 'existing', split_destination_hive_id: '' }))} className="h-9 px-3 text-xs data-[state=on]:bg-amber-600 data-[state=on]:text-white">Existing Hive</Toggle>
                      <Toggle pressed={formData.split_destination === 'new'} onPressedChange={() => setFormData(prev => ({ ...prev, split_destination: 'new', split_destination_hive_id: '' }))} className="h-9 px-3 text-xs data-[state=on]:bg-amber-600 data-[state=on]:text-white">Create New Hive</Toggle>
                    </div>
                    {formData.split_destination === 'existing' && (
                      <Select value={formData.split_destination_hive_id} onValueChange={(val) => setFormData(prev => ({ ...prev, split_destination_hive_id: val }))}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Select destination hive..." /></SelectTrigger>
                        <SelectContent>
                          {emptyHives.map(h => (<SelectItem key={h.id} value={h.id}>Hive #{h.hive_number} {h.nickname ? `(${h.nickname})` : ''} — {h.yard}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}
                    {formData.split_destination === 'new' && (
                      <div className="bg-amber-100 rounded-lg p-3 text-sm text-amber-800">A new hive will be auto-created in <strong>{hive?.yard || 'this yard'}</strong></div>
                    )}
                  </div>
                </div>
              )}

              {/* Requeened Details - fallback */}
              {formData.actions.includes('Requeened') && (
                <div className="mt-4 bg-lime-50 border border-lime-200 rounded-xl p-4 space-y-3">
                  <div className="font-semibold text-lime-800">👑 Requeened Details</div>
                  <div>
                    <Label className="text-xs text-stone-600">Requeened Date</Label>
                    <Input type="date" value={formData.requeened_date} onChange={(e) => setFormData({ ...formData, requeened_date: e.target.value })} className="h-10 mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-lime-800 mb-2 block">Queen Source</Label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {[{ value: 'queen_batch', label: '🧬 From Breeding Batch' }, { value: 'purchased', label: '🛒 Purchased' }, { value: 'emergency_cell', label: '🚨 Emergency Queen Cell' }, { value: 'other', label: '📝 Other' }].map(opt => (
                        <Toggle key={opt.value} pressed={formData.requeened_source === opt.value} onPressedChange={() => setFormData(prev => ({ ...prev, requeened_source: opt.value, requeened_queen_cell_id: '', requeened_batch_id: '' }))} className="h-9 px-3 text-xs data-[state=on]:bg-lime-600 data-[state=on]:text-white">{opt.label}</Toggle>
                      ))}
                    </div>
                    {formData.requeened_source === 'queen_batch' && availableQueens.length > 0 && (
                      <Select value={formData.requeened_queen_cell_id} onValueChange={(val) => { const q = availableQueens.find(q => q.cell_id === val); setFormData(prev => ({ ...prev, requeened_queen_cell_id: val, requeened_batch_id: q?.batch_id || '', requeened_queen_breed: q?.breed || '' })); }}>
                        <SelectTrigger className="h-10"><SelectValue placeholder="Select a queen..." /></SelectTrigger>
                        <SelectContent>{availableQueens.map(q => (<SelectItem key={q.cell_id} value={q.cell_id}>{q.batch_name} — Cell #{q.cell_number} ({q.status}{q.breed ? `, ${q.breed}` : ''})</SelectItem>))}</SelectContent>
                      </Select>
                    )}
                    {formData.requeened_source === 'purchased' && (
                      <Input value={formData.requeened_queen_breed} onChange={(e) => setFormData(prev => ({ ...prev, requeened_queen_breed: e.target.value }))} placeholder="Queen breed/supplier" className="h-10" />
                    )}
                    {(formData.requeened_source === 'emergency_cell' || formData.requeened_source === 'other') && (
                      <Input value={formData.requeened_notes} onChange={(e) => setFormData(prev => ({ ...prev, requeened_notes: e.target.value }))} placeholder="Notes about the new queen..." className="h-10" />
                    )}
                  </div>
                </div>
              )}

              {/* Winter Pack Down Details */}
              {formData.actions.includes('Winter Pack Down') && (
                <div className="mt-4 bg-stone-100 border border-stone-300 rounded-xl p-4">
                  <Label className="text-xs text-stone-600"># of Boxes Packed Down</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.winter_pack_boxes}
                    onChange={(e) => setFormData({ ...formData, winter_pack_boxes: e.target.value })}
                    placeholder="Enter number of boxes"
                    className="h-12 mt-1"
                  />
                </div>
              )}

              {/* Combined Hives Details */}
              {formData.actions.includes('Combined Hives') && (
                <div className="mt-4 bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-4">
                  <div className="font-semibold text-purple-800">🔗 Combined Hives Details</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-stone-600">Primary Hive (receiving bees)</Label>
                      <div className="text-sm text-purple-700 font-medium mt-1 bg-purple-100 p-2 rounded">
                        This Hive ({hive?.hive_number || 'Current'})
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Source Hive (being combined)</Label>
                      <Select 
                        value={formData.combine_source_hive} 
                        onValueChange={(value) => setFormData({ ...formData, combine_source_hive: value })}
                      >
                        <SelectTrigger className="h-12 mt-1">
                          <SelectValue placeholder="Select hive to combine..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allHives
                            .filter(h => h.id !== hive?.id && h.status === 'active')
                            .map(h => (
                              <SelectItem key={h.id} value={h.id}>
                                Hive {h.hive_number} {h.nickname ? `(${h.nickname})` : ''} - {allYards.find(y => y.id === h.yard_id)?.name || 'Unknown Yard'}
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-stone-600">What to do with the empty hive?</Label>
                    <Select 
                      value={formData.combine_empty_hive_action} 
                      onValueChange={(value) => setFormData({ ...formData, combine_empty_hive_action: value })}
                    >
                      <SelectTrigger className="h-12 mt-1">
                        <SelectValue placeholder="Select action for empty hive..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inactive">Mark as Inactive (no longer occupied)</SelectItem>
                        <SelectItem value="storage">Taken Back to Storage</SelectItem>
                        <SelectItem value="disassemble">Disassemble Equipment</SelectItem>
                        <SelectItem value="repurpose">Repurpose for Split/Swarm</SelectItem>
                        <SelectItem value="sell">Marked for Sale</SelectItem>
                        <SelectItem value="other">Other (specify in notes)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-stone-600">Additional Notes</Label>
                    <Textarea
                      value={formData.combine_empty_hive_notes}
                      onChange={(e) => setFormData({ ...formData, combine_empty_hive_notes: e.target.value })}
                      placeholder="e.g., Queen was failing, weak colony combined with strong one..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>

                  <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-sm text-purple-800">
                    ⚠️ <strong>Note:</strong> If you mark the source hive as &quot;Inactive&quot;, it will be removed from the Active Hives count on your dashboard.
                  </div>
                </div>
              )}

              {/* Moved Hive Details */}
              {formData.actions.includes('Moved Hive') && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-4">
                  <div className="font-semibold text-blue-800">📍 Moved Hive Details</div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-stone-600">Type of Move</Label>
                      <Select 
                        value={formData.moved_hive_type} 
                        onValueChange={(value) => setFormData({ ...formData, moved_hive_type: value })}
                      >
                        <SelectTrigger className="h-12 mt-1">
                          <SelectValue placeholder="Select move type..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="same_apiary">Repositioned in Same Apiary</SelectItem>
                          <SelectItem value="different_apiary">Moved to Different Apiary</SelectItem>
                          <SelectItem value="new_location">Moved to New Location/Address</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-stone-600">Reason for Move</Label>
                      <Select 
                        value={formData.moved_hive_reason} 
                        onValueChange={(value) => setFormData({ ...formData, moved_hive_reason: value })}
                      >
                        <SelectTrigger className="h-12 mt-1">
                          <SelectValue placeholder="Select reason..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="better_sun">Better Sun Exposure</SelectItem>
                          <SelectItem value="shade">More Shade Needed</SelectItem>
                          <SelectItem value="wind_protection">Wind Protection</SelectItem>
                          <SelectItem value="flooding">Flooding/Drainage Issues</SelectItem>
                          <SelectItem value="neighbor_complaint">Neighbor Complaint</SelectItem>
                          <SelectItem value="pesticide_spraying">Pesticide Spraying Nearby</SelectItem>
                          <SelectItem value="expansion">Apiary Expansion</SelectItem>
                          <SelectItem value="consolidation">Apiary Consolidation</SelectItem>
                          <SelectItem value="access">Better Access</SelectItem>
                          <SelectItem value="forage">Better Forage Location</SelectItem>
                          <SelectItem value="robbing">Prevent Robbing</SelectItem>
                          <SelectItem value="other">Other (specify in notes)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {formData.moved_hive_type === 'same_apiary' && (
                    <div>
                      <Label className="text-xs text-stone-600">New Position in Apiary</Label>
                      <Input
                        type="text"
                        value={formData.moved_hive_new_position}
                        onChange={(e) => setFormData({ ...formData, moved_hive_new_position: e.target.value })}
                        placeholder="e.g., Row 2, Position 3; Near the oak tree; East side"
                        className="h-12 mt-1"
                      />
                    </div>
                  )}

                  {formData.moved_hive_type === 'different_apiary' && (
                    <div>
                      <Label className="text-xs text-stone-600">New Apiary</Label>
                      <Select 
                        value={formData.moved_hive_new_apiary} 
                        onValueChange={(value) => setFormData({ ...formData, moved_hive_new_apiary: value })}
                      >
                        <SelectTrigger className="h-12 mt-1">
                          <SelectValue placeholder="Select new apiary..." />
                        </SelectTrigger>
                        <SelectContent>
                          {allYards
                            .filter(y => y.id !== hive?.yard_id)
                            .map(y => (
                              <SelectItem key={y.id} value={y.id}>
                                {y.name} {y.address ? `- ${y.address}` : ''}
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs text-stone-600">Additional Notes</Label>
                    <Textarea
                      value={formData.moved_hive_notes}
                      onChange={(e) => setFormData({ ...formData, moved_hive_notes: e.target.value })}
                      placeholder="e.g., Moved 3 meters to the left for better morning sun..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>

                  <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 text-sm text-blue-800">
                    💡 <strong>Tip:</strong> If moving more than 3 feet but less than 3 miles, consider doing it gradually (1-2 feet per day) to prevent foragers from getting lost.
                  </div>
                </div>
              )}

              {/* Dead Out / Lost Colony Details - fallback */}
              {formData.actions.includes('Dead Out / Lost Colony') && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4 space-y-3">
                  <div className="font-semibold text-red-800">💀 Dead Out / Lost Colony</div>
                  <p className="text-xs text-red-600">
                    {formData.dead_out_cause.some(c => ['AFB (American Foulbrood)', 'EFB (European Foulbrood)', 'Pesticide Exposure', 'Nosema', 'Unknown'].includes(c))
                      ? <>This will mark the hive as <strong>Inactive</strong> — equipment may be contaminated.</>
                      : formData.dead_out_cause.length > 0
                        ? <>This will mark the hive as <strong>Ready (Prepared)</strong> — equipment can be reused.</>
                        : <>Select a cause. Reusable causes → Ready; contaminated causes → Inactive.</>
                    }
                  </p>
                  <div>
                    <Label className="text-xs font-semibold text-red-800 mb-2 block">Suspected Cause(s) — select all that apply</Label>
                    <div className="flex flex-wrap gap-2">
                      {['Varroa Mites', 'Small Hive Beetle (SHB)', 'Wax Moths', 'Starvation', 'Queenless', 'Pesticide Exposure', 'Absconded', 'Robbing', 'AFB (American Foulbrood)', 'EFB (European Foulbrood)', 'Nosema', 'Cold / Exposure', 'Unknown'].map(cause => (
                        <Toggle key={cause} pressed={formData.dead_out_cause.includes(cause)} onPressedChange={() => { setFormData(prev => { const causes = prev.dead_out_cause.includes(cause) ? prev.dead_out_cause.filter(c => c !== cause) : [...prev.dead_out_cause, cause]; return { ...prev, dead_out_cause: causes }; }); }} className="h-9 px-3 text-xs data-[state=on]:bg-red-600 data-[state=on]:text-white">{cause}</Toggle>
                      ))}
                    </div>
                  </div>
                  {formData.dead_out_cause.includes('AFB (American Foulbrood)') && (
                    <div className="bg-red-200 border-2 border-red-500 rounded-xl p-4 text-red-900">
                      <div className="font-bold text-base mb-2">🔥 AFB ALERT — Notifiable Disease</div>
                      <ul className="list-disc list-inside space-y-1 text-sm">
                        <li><strong>AFB</strong> is a notifiable disease — report to your local apiary inspector</li>
                        <li>All frames, boxes, and components <strong>must be burned</strong></li>
                        <li>Do <strong>NOT</strong> reuse any equipment — spores survive 50+ years</li>
                        <li>Inspect all neighbouring hives for signs of infection</li>
                        <li>Sterilise hive tools with scorching or bleach solution</li>
                      </ul>
                      <p className="mt-2 font-semibold text-sm">Tasks will be auto-created when you save.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-stone-600">Additional Observations</Label>
                    <Textarea value={formData.dead_out_notes} onChange={(e) => setFormData(prev => ({ ...prev, dead_out_notes: e.target.value }))} placeholder="Describe what you found..." className="mt-1" rows={3} />
                  </div>
                  <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-sm text-red-800">
                    ⚠️ <strong>On save:</strong> Hive #{hive?.hive_number} will be marked as Inactive — {formData.dead_out_cause.length > 0 ? formData.dead_out_cause.join(', ') : 'Dead Out'}
                  </div>
                </div>
              )}


              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mt-4">
                <Label className="text-stone-700 font-medium mb-3 block">Equipment</Label>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-300">
                        <th className="text-left py-2 px-2 font-semibold text-stone-700">Item</th>
                        <th className="text-center py-2 px-2 font-semibold text-lime-700 w-32">Added</th>
                        <th className="text-center py-2 px-2 font-semibold text-red-700 w-32">Removed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: 'Entrance Reducer', addKey: 'Added Entrance Reducer', removeKey: 'Removed Entrance Reducer' },
                        { name: 'Feeder', addKey: 'Added Feeder', removeKey: 'Removed Feeder' },
                        { name: 'Excluder', addKey: 'Added Excluder', removeKey: 'Removed Excluder' },
                      ].map((item) => (
                        <tr key={item.name} className="border-b border-stone-200">
                          <td className="py-2 px-2 font-medium text-stone-700">{item.name}</td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <Toggle
                                pressed={formData.actions.includes(item.addKey)}
                                onPressedChange={() => {
                                  toggleArray('actions', item.addKey);
                                  if (formData.actions.includes(item.removeKey)) toggleArray('actions', item.removeKey);
                                }}
                                className="h-10 w-12 data-[state=on]:bg-lime-600 data-[state=on]:text-white text-xs"
                              >+</Toggle>
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <Toggle
                                pressed={formData.actions.includes(item.removeKey)}
                                onPressedChange={() => {
                                  toggleArray('actions', item.removeKey);
                                  if (formData.actions.includes(item.addKey)) toggleArray('actions', item.addKey);
                                }}
                                className="h-10 w-12 data-[state=on]:bg-red-600 data-[state=on]:text-white text-xs"
                              >-</Toggle>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
          
          {/* Brood Management Section - Dynamic from admin config */}
          {(() => {
            const broodActions = availableActionTypes.filter(a => a.category === 'brood_management');
            if (broodActions.length === 0) return null;
            const addActions = broodActions.filter(a => a.action_type === 'add');
            const removeActions = broodActions.filter(a => a.action_type === 'remove');
            const broodPairs = addActions.map(addAction => {
              const itemName = addAction.name.replace('Added ', '');
              const removeAction = removeActions.find(r => r.name.replace('Removed ', '') === itemName);
              return {
                name: itemName,
                addKey: addAction.name,
                removeKey: removeAction?.name || `Removed ${itemName}`,
                hasQty: addAction.has_qty || removeAction?.has_qty,
                qtyKey: itemName.toLowerCase().replace(/\s+/g, '_') + '_qty'
              };
            });
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4" data-testid="brood-management-section">
                <Label className="text-amber-800 font-semibold mb-3 block flex items-center gap-2">
                  <Box className="h-4 w-4" />
                  Brood Management
                </Label>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-amber-300">
                        <th className="text-left py-2 px-2 font-semibold text-amber-800">Item</th>
                        <th className="text-center py-2 px-2 font-semibold text-lime-700 w-32">Added</th>
                        <th className="text-center py-2 px-2 font-semibold text-red-700 w-32">Removed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {broodPairs.map((item) => (
                        <tr key={item.name} className="border-b border-amber-100">
                          <td className="py-2 px-2 font-medium text-stone-700">{item.name}</td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <Toggle pressed={formData.actions.includes(item.addKey)} onPressedChange={() => { toggleArray('actions', item.addKey); if (formData.actions.includes(item.removeKey)) toggleArray('actions', item.removeKey); }} className="h-10 w-12 data-[state=on]:bg-lime-600 data-[state=on]:text-white text-xs">+</Toggle>
                              {item.hasQty && formData.actions.includes(item.addKey) && (
                                <Input type="number" min="1" value={formData[`added_${item.qtyKey}`] || ''} onChange={(e) => setFormData({ ...formData, [`added_${item.qtyKey}`]: e.target.value })} placeholder="#" className="h-10 w-14 text-center text-xs" />
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <Toggle pressed={formData.actions.includes(item.removeKey)} onPressedChange={() => { toggleArray('actions', item.removeKey); if (formData.actions.includes(item.addKey)) toggleArray('actions', item.addKey); }} className="h-10 w-12 data-[state=on]:bg-red-600 data-[state=on]:text-white text-xs">-</Toggle>
                              {item.hasQty && formData.actions.includes(item.removeKey) && (
                                <Input type="number" min="1" value={formData[`removed_${item.qtyKey}`] || ''} onChange={(e) => setFormData({ ...formData, [`removed_${item.qtyKey}`]: e.target.value })} placeholder="#" className="h-10 w-14 text-center text-xs" />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Super Management Section - Dynamic from admin config, filtered by hive type */}
          {(() => {
            const ht = (hive?.hive_stack?.hive_type || '').toLowerCase().replace(/\s+/g, '_');
            const allSuperActions = availableActionTypes.filter(a => a.category === 'super_management');
            // Filter by hive type: show items matching this hive type, or items with no hive_types restriction
            const superActions = allSuperActions.filter(a => {
              if (!a.hive_types || a.hive_types.length === 0) return true;
              if (!ht) return a.hive_types.includes('langstroth'); // default to langstroth
              return a.hive_types.some(t => ht.includes(t));
            });
            if (superActions.length === 0) return null;
            const addActions = superActions.filter(a => a.action_type === 'add');
            const removeActions = superActions.filter(a => a.action_type === 'remove');
            const superPairs = addActions.map(addAction => {
              const itemName = addAction.name.replace('Added ', '');
              const removeAction = removeActions.find(r => r.name.replace('Removed ', '') === itemName);
              return {
                name: itemName,
                addKey: addAction.name,
                removeKey: removeAction?.name || `Removed ${itemName}`,
                hasQty: addAction.has_qty || removeAction?.has_qty,
                qtyKey: itemName.toLowerCase().replace(/\s+/g, '_') + '_qty',
                note: addAction.hive_types && !addAction.hive_types.some(t => ht.includes(t) && t === ht.split('_')[0]) ? 'compatible' : null
              };
            });
            return (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4" data-testid="super-management-section">
                <Label className="text-blue-800 font-semibold mb-3 block flex items-center gap-2">
                  <Layers className="h-4 w-4" />
                  Super Management
                  {hive?.hive_stack?.hive_type && (
                    <span className="text-xs font-normal text-blue-500 bg-blue-100 px-2 py-0.5 rounded-full">{hive.hive_stack.hive_type}</span>
                  )}
                </Label>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-blue-300">
                        <th className="text-left py-2 px-2 font-semibold text-blue-800">Super Type</th>
                        <th className="text-center py-2 px-2 font-semibold text-lime-700 w-32">Added</th>
                        <th className="text-center py-2 px-2 font-semibold text-red-700 w-32">Removed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {superPairs.map((item) => (
                        <tr key={item.name} className="border-b border-blue-100">
                          <td className="py-2 px-2 font-medium text-stone-700">
                            {item.name}
                            {item.note && <span className="text-xs text-blue-400 ml-1">({item.note})</span>}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <Toggle pressed={formData.actions.includes(item.addKey)} onPressedChange={() => { toggleArray('actions', item.addKey); if (formData.actions.includes(item.removeKey)) toggleArray('actions', item.removeKey); }} className="h-10 w-12 data-[state=on]:bg-lime-600 data-[state=on]:text-white text-xs">+</Toggle>
                              {item.hasQty && formData.actions.includes(item.addKey) && (
                                <Input type="number" min="1" value={formData[`added_${item.qtyKey}`] || ''} onChange={(e) => setFormData({ ...formData, [`added_${item.qtyKey}`]: e.target.value })} placeholder="#" className="h-10 w-14 text-center text-xs" />
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <Toggle pressed={formData.actions.includes(item.removeKey)} onPressedChange={() => { toggleArray('actions', item.removeKey); if (formData.actions.includes(item.addKey)) toggleArray('actions', item.addKey); }} className="h-10 w-12 data-[state=on]:bg-red-600 data-[state=on]:text-white text-xs">-</Toggle>
                              {item.hasQty && formData.actions.includes(item.removeKey) && (
                                <Input type="number" min="1" value={formData[`removed_${item.qtyKey}`] || ''} onChange={(e) => setFormData({ ...formData, [`removed_${item.qtyKey}`]: e.target.value })} placeholder="#" className="h-10 w-14 text-center text-xs" />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
          <div className="mt-4 pt-4 border-t border-stone-200">
            {!showSuggestAction ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowSuggestAction(true)}
                className="text-lime-600 border-lime-300 hover:bg-lime-50"
              >
                <Lightbulb className="w-4 h-4 mr-2" />
                Suggest New Action
              </Button>
            ) : (
              <div className="bg-lime-50 border border-lime-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-lime-800 font-medium">
                  <Lightbulb className="w-4 h-4" />
                  Suggest a New Action
                </div>
                <p className="text-xs text-lime-700">Your suggestion will be sent to the admin for approval.</p>
                <div className="flex gap-2">
                  <Input
                    value={suggestActionName}
                    onChange={(e) => setSuggestActionName(e.target.value)}
                    placeholder="Action name (e.g., 'Cleaned Bottom Board')"
                    className="flex-1"
                  />
                  <Select value={suggestActionCategory} onValueChange={setSuggestActionCategory}>
                    <SelectTrigger className="w-36">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {actionCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSuggestAction}
                    disabled={submittingSuggestion}
                    className="bg-lime-600 hover:bg-lime-700"
                  >
                    {submittingSuggestion ? 'Submitting...' : 'Submit Suggestion'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowSuggestAction(false); setSuggestActionName(''); }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Section>
  );
};

export default ActionsSection;
